import React, { useState, useEffect } from 'react';
import SkillPill from 'src/components/common/SkillPill';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import Section from 'src/components/ui/section';
import {
  Mail,
  Phone,
  CalendarDays,
  Loader2,
  MapPin,
  GraduationCap,
  Briefcase,
  Award,
  IndianRupee,
  Settings2,
  Download,
  User,
  ArrowLeft,
  Search,
} from 'lucide-react';
import { format, parse } from 'date-fns';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ResumePDF from 'src/pages/ResumePDF/ResumePDF';
import { useCandidateDetails } from 'src/services/api/queries/searchQueries';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from 'src/components/ui/tabs';
import { Input } from 'src/components/ui/input';
import AppSelect from 'src/components/common/AppSelect';
import { CandidateService } from 'src/services/api/CandidateService';
import { useToast } from 'src/hooks/use-toast';

const CandidateInfo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [resumeData, setResumeData] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');

  // Audit log states
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditSearch, setAuditSearch] = useState('');
  const [eventCategory, setEventCategory] = useState('');
  const [actorType, setActorType] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [auditConfig, setAuditConfig] = useState({
    event_categories: {},
    actor_types: {},
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
  });

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Candidates', href: '/candidates/search' },
    { label: 'Profile', href: `/candidate/${id}` },
  ];

  const getQualificationDisplayName = (qualificationType) => {
    switch (qualificationType) {
      case 'TENTH':
        return 'Secondary';
      case 'TWELFTH':
        return 'Higher secondary';
      case 'DEGREE':
        return "Bachelor's Degree";
      case 'MASTERS':
        return "Master's Degree";
      default:
        return qualificationType;
    }
  };

  const { data: response, isLoading, error } = useCandidateDetails(id);
  const candidateData = response?.data?.profile_data;
  const regId = response?.data?.registration_id;

  // Data mapping function
  const mapCandidateDataToResumePDF = (candData) => {
    if (!candData) return {};

    // Extract personal information
    const personal = candData.personal_information || {};
    const address = personal.address || {};

    // Parse work experience dates
    const mapWorkExperiences = (experiences) =>
      (experiences || []).map((exp) => {
        // Parse dates (format: "MM/YYYY")
        const fromDate = exp.from_date ? exp.from_date.split('/') : [];
        const toDate = exp.to_date ? exp.to_date.split('/') : [];

        return {
          company: exp.company_name,
          role: exp.role,
          employment_period: {
            startMonth: fromDate[0] || '',
            startYear: fromDate[1] || '',
            endMonth: toDate[0] || '',
            endYear: toDate[1] || '',
          },
        };
      });

    // Map educational details
    const mapEducations = (educations) =>
      (educations || []).map((edu) => ({
        institution: edu.institution,
        qualification: edu.qualification_type,
        graduationYear: edu.graduation_year,
        calculatedScore: edu.score_percentage,
      }));

    // ResumePDF renders skills/certifications as plain <Text> chips — it
    // can't handle the richer {id, name, type} reference-object shape these
    // sometimes come back as (crashes react-pdf with "Objects are not
    // valid as a React child").
    const toLabel = (item, ...keys) => {
      if (typeof item === 'string') return item;
      return keys.reduce((label, key) => label || item?.[key] || '', '');
    };

    // Create the transformed data object
    return {
      firstname: personal.first_name || '',
      middlename: personal.middle_name || '',
      lastname: personal.last_name || '',
      email: personal.email || '',
      contact: personal.phone || '',
      dob: personal.dob || '',

      // Address information
      addressLine1: address.address_line_1 || '',
      city: address.city || '',
      state: address.state || '',
      zipCode: address.zip_code || '',

      // Education, experience, skills and certifications
      educations: mapEducations(candData.educational_details),
      workExperiences: mapWorkExperiences(candData.work_experiences),
      skills: (candData.skills || [])
        .map((s) => toLabel(s, 'skill_name', 'name'))
        .filter(Boolean),
      otherSkills: candData.other_details?.other_skills || '',
      certifications: (candData.certifications || [])
        .map((c) => toLabel(c, 'certification_name', 'name'))
        .filter(Boolean),
      otherCertifications: candData.other_details?.other_certifications || '',
    };
  };

  // Update resume data when candidate data changes
  React.useEffect(() => {
    if (candidateData) {
      const mappedResumeData = mapCandidateDataToResumePDF(candidateData);
      setResumeData(mappedResumeData);
    }
  }, [candidateData]);

  const loadAuditConfig = async () => {
    try {
      const configResponse = await CandidateService.getAuditConfig();
      if (configResponse.success) {
        setAuditConfig(
          configResponse.data.audit_configs || {
            event_categories: {},
            actor_types: {},
          }
        );
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load audit configuration',
        variant: 'destructive',
      });
    }
  };

  const loadAuditLogs = async () => {
    try {
      setAuditLogsLoading(true);
      const auditResponse = await CandidateService.getCandidateAuditLogs({
        candidateId: id,
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: auditSearch,
        eventCategory,
        actorType,
        sortBy,
      });

      if (auditResponse.success) {
        setAuditLogs(auditResponse.data.audit_logs || []);
        setPagination(
          auditResponse.data.pagination || {
            total: 0,
            page: 1,
            pageSize: 10,
            totalPages: 1,
            hasNext: false,
            hasPrevious: false,
          }
        );
      } else {
        throw new Error(auditResponse.message || 'Failed to load audit logs');
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setAuditLogsLoading(false);
    }
  };

  // Load audit config once when audit tab is activated
  useEffect(() => {
    if (activeTab === 'audit') {
      loadAuditConfig();
    }
  }, [activeTab]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (activeTab === 'audit') {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [auditSearch, eventCategory, actorType, sortBy]);

  // Load audit logs when filters change
  useEffect(() => {
    if (activeTab === 'audit') {
      loadAuditLogs();
    }
  }, [
    activeTab,
    pagination.page,
    auditSearch,
    eventCategory,
    actorType,
    sortBy,
  ]);

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-muted">
        <Loader2 className="h-8 w-8 animate-spin ahBlueText" />
        <p className="mt-4 text-sm text-gray-600">Loading candidate data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-muted">
        <p className="text-red-500">
          {error.message || 'Failed to load candidate data'}
        </p>
      </div>
    );
  }

  if (!candidateData) {
    return <div>No candidate data found.</div>;
  }

  const {
    personal_information: personalInfo,
    educational_details: educationalDetails,
    work_experiences: workExperiences,
    skills,
    certifications,
  } = candidateData;

  const formatScore = (score) => {
    if (!score) return '';

    // Convert to number and handle any potential string inputs
    const numScore = Number(score);

    // Check if it's a whole number
    if (Number.isInteger(numScore)) {
      return numScore.toString();
    }

    // If it has decimals, return as is (it will automatically remove trailing zeros)
    return numScore.toString();
  };

  const formatAuditDate = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return format(date, 'MMM dd, yyyy, hh:mm a');
    } catch {
      return timestamp;
    }
  };

  const getEventIcon = (eventLabel) => {
    const firstLetter = eventLabel ? eventLabel.charAt(0).toUpperCase() : 'I';
    return firstLetter;
  };

  return (
    <div>
      <PageHeader
        breadcrumbs={breadcrumbItems}
        actions={
          <>
            <Button
              variant="outline"
              size="headerSm"
              className="gap-1.5"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
              Back
            </Button>
            {resumeData && activeTab === 'profile' && (
              <PDFDownloadLink
                document={<ResumePDF data={resumeData} />}
                fileName={`${personalInfo.first_name || ''}_${personalInfo.last_name || ''}_resume.pdf`}
                className="no-underline"
              >
                {({ loading }) => (
                  <Button
                    variant="outline"
                    size="headerSm"
                    className="gap-1.5"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                        Generating PDF...
                      </>
                    ) : (
                      <>
                        Download PDF
                        <Download className="h-3 w-3 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </PDFDownloadLink>
            )}
          </>
        }
      />

      <PageContainer className="p-4 space-y-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-[200px] grid-cols-2 bg-blue-100">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="audit">Audit Log</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-4">
            {/* Personal Information */}
            <Section
              title="Profile"
              icon={<User className="h-5 w-5 ahBlueText" />}
            >
              <div>
                <div className="flex flex-col md:flex-row gap-8 items-start mb-4">
                  <div className="w-16 h-16 rounded-full md:flex hidden items-center justify-center text-blue-700 text-md bg-blue-50 font-normal flex-shrink-0">
                    {`${personalInfo.first_name?.[0]}${personalInfo.last_name?.[0]}`}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 w-full">
                    <div className="flex flex-col gap-2">
                      <span className="text-xs text-gray-500 text-left">
                        Name
                      </span>
                      <h1 className="text-sm font-bold mb-2 text-foreground-strong text-left">
                        {`${personalInfo.first_name} ${personalInfo.last_name}`}
                      </h1>
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="text-xs text-gray-500 text-left">
                        Candidate ID
                      </span>
                      <h1 className="text-sm font-normal mb-2 text-foreground-strong text-left">
                        {regId && (
                          <span className="text-sm text-foreground-strong">
                            #{regId}
                          </span>
                        )}
                      </h1>
                    </div>
                    {personalInfo.dob && (
                      <div className="flex flex-col gap-2">
                        <span className="text-xs text-gray-500 text-left">
                          Date of Birth
                        </span>
                        <p className="text-sm mb-2 text-foreground-strong text-left">
                          <span className="flex items-center gap-2">
                            <CalendarDays className="h-4 w-4 text-gray-500" />
                            {format(
                              parse(personalInfo.dob, 'dd/MM/yyyy', new Date()),
                              'dd MMM yyyy'
                            )}
                          </span>
                        </p>
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <span className="text-xs text-gray-500 text-left">
                        Phone
                      </span>
                      <p className="text-sm mb-2 text-foreground-strong text-left">
                        <span className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-gray-500" />
                          {personalInfo.phone}
                        </span>
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="text-xs text-gray-500 text-left">
                        Email
                      </span>
                      <p className="text-sm mb-2 text-foreground-strong text-left">
                        <span className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-gray-500" />
                          {personalInfo.email}
                        </span>
                      </p>
                    </div>
                    {personalInfo.address && (
                      <div className="flex flex-col gap-2">
                        <span className="text-xs text-gray-500 text-left">
                          Address
                        </span>
                        <p className="text-sm mb-2 text-foreground-strong text-left">
                          <span className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-gray-500" />
                            {`${personalInfo.address.address_line_1}, ${personalInfo.address.city}, ${personalInfo.address.state} ${personalInfo.address.zip_code}`}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Section>

            {/* Work Experience */}
            {workExperiences?.length > 0 && (
              <Section
                title="Work Experience"
                icon={<Briefcase className="h-5 w-5 ahBlueText" />}
              >
                {workExperiences.map((exp) => (
                  <div
                    key={`${exp.company_name}-${exp.from_date}`}
                    className="p-4 rounded-xl mb-4 last:mb-0 text-left bg-card border border-border flex justify-between"
                  >
                    <div>
                      <h3 className="text-md font-semibold text-foreground-strong">
                        {exp.role || ''}
                      </h3>
                      <div className="text-gray-600">{exp.company_name}</div>
                    </div>
                    <div className="text-sm text-gray-500 mt-2">
                      {`${exp.from_date} - ${exp.to_date || 'Present'}`}
                      {exp.is_current && ' (Current)'}
                    </div>
                  </div>
                ))}
              </Section>
            )}

            {/* Education */}
            {educationalDetails?.length > 0 && (
              <Section
                title="Education"
                icon={<GraduationCap className="h-5 w-5 ahBlueText" />}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                  {educationalDetails.map((edu) => (
                    <div
                      key={`${edu.institution}-${edu.graduation_year}`}
                      className="bg-card border border-blue-200 dark:border-blue-900/50 p-4 rounded-xl"
                    >
                      <div className="flex justify-between items-center mb-1">
                        <h3 className="text-md font-semibold text-foreground-strong">
                          {getQualificationDisplayName(edu.qualification_type)}
                        </h3>
                        <p className="text-xs text-gray-600 border border-gray-200 px-2 py-1 rounded-md">
                          {edu.graduation_year}
                        </p>
                      </div>
                      <p>{edu.institution}</p>
                      <div className="flex flex-col justify-between text-xs text-gray-600">
                        <div>
                          {edu.university && <span>{edu.university}</span>}
                          {edu.board_type && (
                            <span>Board: {edu.board_type}</span>
                          )}
                        </div>
                        <div className="text-foreground-strong text-sm font-semibold mt-1">
                          {`Score : ${formatScore(edu.score_percentage)}%`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Skills & Certifications */}
            {(skills?.length > 0 || certifications?.length > 0) && (
              <Section
                title="Skills & Certifications"
                icon={<Award className="h-5 w-5 ahBlueText" />}
              >
                <div className="flex justify-between gap-2">
                  {skills?.length > 0 && (
                    <div className="flex flex-col gap-1 w-full md:w-1/2">
                      <p className="text-xs text-left text-gray-600 ml-2">
                        Skills
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {skills.map((skill, i) => {
                          const label =
                            typeof skill === 'string'
                              ? skill
                              : skill?.name || '';
                          return (
                            <SkillPill
                              key={`skill-${label || i}`}
                              label={label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {certifications?.length > 0 && (
                    <div className="flex flex-col gap-1 w-full md:w-1/2">
                      <p className="text-xs text-left text-gray-600 ml-2">
                        Certifications
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {certifications.map((cert) => (
                          <span
                            key={`cert-${cert}`}
                            className="px-4 py-2 text-gray-600 rounded-full border border-blue-500/50 text-xs bg-blue-50 text-blue-700"
                          >
                            {cert}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Section>
            )}
            {/* Preferences */}
            <Section
              title="Preferences"
              icon={<Settings2 className="h-5 w-5 ahBlueText" />}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                {personalInfo?.current_ctc && (
                  <div className="bg-[#F8FAFC] rounded-xl py-2 px-4 border border-gray-200">
                    <div className="font-semibold text-gray-700">
                      <div className="text-md font-semibold text-foreground-strong">
                        Current CTC
                      </div>
                      <div className="flex gap-[0.5rem] items-center">
                        Annual :
                        <div className="flex gap-1 items-center">
                          <IndianRupee className="h-4 w-4 ahBlueText" />
                          <span className="text-grey-500">
                            {Intl.NumberFormat(navigator.languages).format(
                              personalInfo.current_ctc
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {personalInfo?.expected_ctc && (
                  <div className="bg-[#F8FAFC] rounded-xl py-2 px-4 border border-gray-200">
                    <div className="font-semibold text-gray-700">
                      <div className="text-md font-semibold text-foreground-strong">
                        Expected CTC
                      </div>
                      <div className="flex gap-[0.5rem] items-center">
                        Annual :
                        <div className="flex gap-1 items-center">
                          <IndianRupee className="h-4 w-4 ahBlueText" />
                          <span className="text-grey-500">
                            {Intl.NumberFormat(navigator.languages).format(
                              personalInfo.expected_ctc
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {Array.isArray(personalInfo.preferred_work_locations) &&
                  personalInfo.preferred_work_locations.length > 0 && (
                    <div className="bg-[#F8FAFC] p-4 rounded-xl md:col-span-2 border border-gray-200">
                      <div className="text-md font-semibold text-foreground-strong">
                        Preferred Locations
                      </div>
                      <div className="text-foreground-strong">
                        {personalInfo.preferred_work_locations
                          .map((loc) => loc.name || loc.id)
                          .join(', ')}
                      </div>
                    </div>
                  )}
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="audit" className="space-y-4">
            {/* Audit Log Filters */}
            <div className="bg-card rounded-lg border border-border p-4">
              <h3 className="text-md font-semibold mb-4 text-left">
                Audit Filters
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search in event / summary / description"
                    value={auditSearch}
                    onChange={(e) => setAuditSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <AppSelect
                  value={eventCategory || 'all'}
                  onValueChange={(val) =>
                    setEventCategory(val === 'all' ? '' : val)
                  }
                  options={[
                    { value: 'all', label: 'All categories' },
                    ...Object.entries(auditConfig.event_categories || {}).map(
                      ([key, val]) => ({ value: key, label: val })
                    ),
                  ]}
                  placeholder="All categories"
                />
                <AppSelect
                  value={actorType || 'all'}
                  onValueChange={(val) =>
                    setActorType(val === 'all' ? '' : val)
                  }
                  options={[
                    { value: 'all', label: 'All actor types' },
                    ...Object.entries(auditConfig.actor_types || {}).map(
                      ([key, val]) => ({ value: key, label: val })
                    ),
                  ]}
                  placeholder="All actor types"
                />
                <AppSelect
                  value={sortBy}
                  onValueChange={setSortBy}
                  options={[
                    { value: 'newest', label: 'Newest first' },
                    { value: 'oldest', label: 'Oldest first' },
                  ]}
                  placeholder="Newest first"
                />
              </div>
              <div className="flex justify-between items-center mt-4">
                <p className="text-sm text-gray-600">
                  Showing{' '}
                  <span className="font-semibold">{auditLogs.length}</span>{' '}
                  events
                  {pagination.total > 0 && (
                    <span>
                      {' '}
                      Latest:{' '}
                      {auditLogs.length > 0 &&
                        formatAuditDate(auditLogs[0]?.event_timestamp)}
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Audit Log List */}
            {auditLogsLoading && (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-6 w-6 animate-spin ahBlueText" />
              </div>
            )}

            {!auditLogsLoading && auditLogs.length === 0 && (
              <div className="bg-card rounded-lg border border-border p-8 text-center">
                <p className="text-muted-foreground">No audit logs found</p>
              </div>
            )}

            {!auditLogsLoading && auditLogs.length > 0 && (
              <div className="space-y-4">
                {auditLogs.map((log) => (
                  <div
                    key={log.id}
                    className="bg-card rounded-lg border border-border p-4"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-950/40 flex items-center justify-center text-blue-600 dark:text-blue-400 font-semibold flex-shrink-0">
                        {getEventIcon(log.event_label)}
                      </div>
                      <div className="flex-1 text-left">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-foreground-strong">
                            {log.event_label}
                          </h4>
                          <span className="text-xs text-gray-500">
                            {formatAuditDate(log.event_timestamp)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {log.event_description}
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          <span className="px-3 py-1 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">
                            {log.event_category?.replace(/_/g, ' ')}
                          </span>
                          <span className="px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-700 border border-gray-200">
                            {log.actor_type}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </PageContainer>
    </div>
  );
};

export default CandidateInfo;
